import React from 'react';
import { Link } from 'react-router-dom';
import {
  FiCheckCircle,
  FiSearch,
  FiSliders,
  FiUsers,
  FiFileText,
  FiShield,
  FiTruck,
  FiArrowRight,
} from 'react-icons/fi';
import { Page, LayoutComposer } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './HowItWorksPage.module.css';

const STEPS = [
  {
    number: 1,
    icon: <FiSearch />,
    title: 'Step 1 — Discover Products',
    tag: 'Catalogue Discovery',
    desc: 'Browse available products and manufacturing opportunities sourced from verified industrial clusters.',
    points: [
      'Factories provide comprehensive specs, tiered pricing, and lead times.',
      'Factory identities & direct contacts remain protected to prevent fraud.',
      'Each product is represented by an assigned, verified local Sourcing Agent.',
      'Real-time production capacity and MOQ specifications listed clearly.',
    ],
  },
  {
    number: 2,
    icon: <FiSliders />,
    title: 'Step 2 — Select Your Quantity',
    tag: 'MOQ Analysis',
    desc: 'Choose the exact quantity your business requires without worrying about high manufacturer barriers.',
    points: [
      'System automatically compares your quantity against Factory Minimum Order Quantity (MOQ).',
      'If quantity meets or exceeds MOQ, proceed immediately to a direct quotation.',
      'If quantity is below MOQ, ChinaDolphins instantly offers the Order Aggregation pathway.',
    ],
  },
  {
    number: 3,
    icon: <FiUsers />,
    title: 'Step 3 — Join Order Aggregation',
    tag: 'Group Buying Power',
    desc: 'Your required quantity is combined digitally with demand from other buyers interested in the same product to reach the factory MOQ.',
    isAggregation: true,
    points: [
      'Demand aggregation combines quantities for the same product, unlocking volume pricing without physical container consolidation.',
      'Total purchasing volume increases collectively to satisfy factory minimum order thresholds.',
      'Follow live pool progress indicators and remaining time directly from your dashboard.',
      'Once the threshold is reached, final factory volume pricing is locked in.',
    ],
  },
  {
    number: 4,
    icon: <FiFileText />,
    title: 'Step 4 — Quotation & Confirmation',
    tag: 'Transparent Terms',
    desc: 'Participating buyers receive a confirmed quotation based on the locked aggregated volume tier.',
    points: [
      'Review confirmed unit price, total cost, lead time, and assigned Sourcing Agent.',
      'All commercial conditions, delivery terms, and QC milestones are laid out clearly.',
      'Confirm acceptance and lock in your order allocation.',
    ],
  },
  {
    number: 5,
    icon: <FiShield />,
    title: 'Step 5 — Production & Quality Control',
    tag: 'On-Site Oversight',
    desc: 'Once commercial terms are confirmed and payment is authorized through approved payment channels, manufacturing commences under local supervision.',
    points: [
      'Assigned Sourcing Agent coordinates directly with factory floor managers.',
      'Production milestones are monitored and logged with real-time status updates.',
      'Mandatory pre-shipment Quality Control (QC) inspection performed before dispatch.',
      'Photos, test logs, and inspection certificates uploaded for buyer sign-off.',
    ],
  },
  {
    number: 6,
    icon: <FiTruck />,
    title: 'Step 6 — Export & Shipment',
    tag: 'Cross-Border Delivery',
    desc: 'Full customs clearance and international logistics documentation are prepared smoothly.',
    points: [
      'China export customs declarations and Bill of Lading (B/L) issued.',
      'Verified QC documentation and container tracking details provided.',
      'Buyer receives live freight tracking updates all the way to port or destination warehouse.',
    ],
  },
];

const HowItWorksPage = () => {
  const layoutAreas = `
    topbar
    main
    footer
  `;

  return (
    <Page
      title="How It Works | ChinaDolphins B2B Sourcing"
      description="Learn how ChinaDolphins combines Order Aggregation technology with professional on-the-ground sourcing agents in China to simplify international procurement."
      className={css.root}
    >
      <LayoutComposer areas={layoutAreas}>
        {props => {
          const { Topbar, Main, Footer } = props;
          return (
            <>
              <Topbar as="header">
                <TopbarContainer />
              </Topbar>

              <Main as="main" id="main-content">
                {/* ── Hero ── */}
                <section className={css.hero}>
                  <div className={css.heroInner}>
                    <div className={css.heroBadge}>
                      <FiSliders /> The 6-Step Sourcing Workflow
                    </div>
                    <h1 className={css.heroTitle}>
                      Group Buying Power. Factory Pricing. Local Support.
                    </h1>
                    <p className={css.heroSubtitle}>
                      ChinaDolphins combines intelligent marketplace technology with verified on-the-ground sourcing agents in China to simplify cross-border procurement.
                    </p>
                  </div>
                </section>

                {/* ── Stepper Section ── */}
                <section className={css.section}>
                  <div className={css.stepsContainer}>
                    {STEPS.map(step => (
                      <div key={step.number} className={css.stepCard}>
                        <div className={css.stepBadgeCol}>
                          <div className={css.stepNumber}>{step.number}</div>
                        </div>

                        <div className={css.stepContent}>
                          <div className={css.stepHeader}>
                            <h3 className={css.stepTitle}>{step.title}</h3>
                            <span className={`${css.stepTag} ${step.isAggregation ? css.stepTagHighlight : ''}`}>
                              {step.tag}
                            </span>
                          </div>

                          <p className={css.stepDesc}>{step.desc}</p>

                          <ul className={css.stepPoints}>
                            {step.points.map((point, idx) => (
                              <li key={idx} className={css.stepPoint}>
                                <FiCheckCircle className={css.checkIcon} />
                                <span>{point}</span>
                              </li>
                            ))}
                          </ul>

                          {step.isAggregation && (
                            <div className={css.simBox}>
                              <div className={css.simHeader}>
                                <span>Live Aggregation Pool Progress Simulation</span>
                                <span>850 / 1,000 Units (85%)</span>
                              </div>
                              <div className={css.simBarTrack}>
                                <div className={css.simBarFill} />
                              </div>
                              <div className={css.simFooter}>
                                <span>4 Buyers Participating</span>
                                <span><strong>Tier 2 Discount Unlocked: 25% Off</strong></span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* ── CTA Banner ── */}
                  <div className={css.ctaBanner}>
                    <h2 className={css.ctaTitle}>Ready to Start Sourcing Smarter?</h2>
                    <p className={css.ctaSubtitle}>
                      Browse verified products or register your company to participate in active order aggregations.
                    </p>
                    <div className={css.ctaBtns}>
                      <Link to="/s" className={css.ctaPrimary}>
                        Browse Catalogue <FiArrowRight style={{ marginLeft: 6 }} />
                      </Link>
                      <Link to="/signup" className={css.ctaSecondary}>
                        Create Buyer Account
                      </Link>
                    </div>
                  </div>
                </section>
              </Main>

              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </Page>
  );
};

export default HowItWorksPage;
